import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

const COLORS = { primaryDark: "#08263e" };

export const SettingsModal = ({ costConfig, analytics, onSave, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Cost Configuration</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>&times;</Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => {
          e.preventDefault();
          const f = e.target;
          onSave({
            input_token_cost_per_1k: parseFloat(f.input_cost.value),
            output_token_cost_per_1k: parseFloat(f.output_cost.value),
            credit_rate_usd: parseFloat(f.credit_rate.value),
            credits_per_dollar: parseFloat(f.credits_per_dollar.value),
            currency: f.currency.value,
            session_cost_limit_enabled: f.session_limit_toggle.checked,
            max_cost_per_session: parseFloat(f.max_cost_session.value)
          });
        }}>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">Emergent Credit Rate</p>
              <p className="text-xs">Based on your purchase: 6,000 credits = $1,000</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Credit Rate ($/credit)</label>
                <input name="credit_rate" type="number" step="0.0001" defaultValue={costConfig.credit_rate_usd || 0.1667} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Credits per $1</label>
                <input name="credits_per_dollar" type="number" step="0.1" defaultValue={costConfig.credits_per_dollar || 6.0} className="w-full px-4 py-2 border rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Input Token Cost (per 1K)</label>
              <input name="input_cost" type="number" step="0.001" defaultValue={costConfig.input_token_cost_per_1k} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Output Token Cost (per 1K)</label>
              <input name="output_cost" type="number" step="0.001" defaultValue={costConfig.output_token_cost_per_1k} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Display Currency</label>
              <select name="currency" defaultValue={costConfig.currency} className="w-full px-4 py-2 border rounded-lg">
                <option value="USD">USD ($)</option>
                <option value="PHP">PHP (PHP)</option>
              </select>
            </div>
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium">Session Cost Limit</p>
                  <p className="text-xs text-gray-500">Block LLM calls when session cost exceeds threshold</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-testid="session-limit-toggle">
                  <input type="checkbox" name="session_limit_toggle" defaultChecked={costConfig.session_cost_limit_enabled || false} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Cost per Session (USD)</label>
                <input name="max_cost_session" type="number" step="0.01" min="0.01" defaultValue={costConfig.max_cost_per_session || 0.50} className="w-full px-4 py-2 border rounded-lg" data-testid="max-cost-session-input" />
                <p className="text-xs text-gray-400 mt-1">FAQ responses still go through. Only pure LLM calls are blocked.</p>
              </div>
            </div>
            {analytics?.exchange_rate && (
              <p className="text-xs text-[#999]">Current USD/PHP rate: {analytics.exchange_rate.usd_php} (auto-updated daily from {analytics.exchange_rate.source})</p>
            )}
            <Button type="submit" className="w-full text-white" style={{ backgroundColor: COLORS.primaryDark }}>Save Configuration</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  </div>
);

export const ResetModal = ({ resetPhrase, setResetPhrase, onReset, onClose, loading }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-md border-red-200" data-testid="reset-modal">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2 text-red-600">
          <AlertTriangle size={20} /> Reset All Statistics
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>&times;</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium mb-1">This action cannot be undone!</p>
            <p className="text-sm text-red-700">This will permanently delete all chat analytics, session history, and heatmap data.</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Type <code className="bg-gray-100 px-2 py-0.5 rounded text-red-600 font-bold">RESET ALL DATA</code> to confirm:
            </label>
            <input type="text" value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder="Type confirmation phrase..." className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300" data-testid="reset-confirmation-input" autoComplete="off" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={resetPhrase !== "RESET ALL DATA" || loading} onClick={onReset} data-testid="reset-confirm-btn">
              {loading ? "Resetting..." : "Reset All Data"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);
